<div class="dashboard p-16">

  <!-- ── Date Range Bar ─────────────────────────────────────────────────────── -->
  <div class="date-range-bar m-b-16 compact-fields">
    <div class="preset-group">
      <button
        *ngFor="let p of presets"
        mat-button
        class="preset-btn"
        [class.active]="activePreset === p.key"
        (click)="applyPreset(p)">
        {{ p.label }}
      </button>
    </div>
    <div class="range-divider"></div>
    <form [formGroup]="rangeForm" class="date-range-form">
      <mat-form-field appearance="outline" class="date-range-field" subscriptSizing="dynamic">
        <mat-label>Custom range</mat-label>
        <mat-date-range-input [formGroup]="rangeForm" [rangePicker]="picker" [min]="minDate" [max]="maxDate">
          <input matStartDate formControlName="start" placeholder="Start date">
          <input matEndDate formControlName="end" placeholder="End date" (dateChange)="onCustomRangeApply()">
        </mat-date-range-input>
        <mat-datepicker-toggle matIconSuffix [for]="picker"></mat-datepicker-toggle>
        <mat-date-range-picker #picker></mat-date-range-picker>
      </mat-form-field>
    </form>
  </div>

  <div *ngIf="loading" class="d-flex justify-content-center p-y-48">
    <mat-spinner diameter="40"></mat-spinner>
  </div>

  <ng-container *ngIf="!loading">

    <!-- ── Operational KPIs ──────────────────────────────────────────────────── -->
    <div class="row m-b-16">

      <div class="col-lg-3 col-sm-6 m-b-16">
        <mat-card class="cardWithShadow kpi-card">
          <mat-card-content>
            <div class="kpi-icon bg-light-primary">
              <mat-icon class="text-primary">groups</mat-icon>
            </div>
            <div class="kpi-body">
              <div class="kpi-value">{{ kpis?.activeStudents ?? 0 }}</div>
              <div class="kpi-label">Students</div>
              <div class="kpi-sub text-muted">with a booking in the period</div>
            </div>
          </mat-card-content>
        </mat-card>
      </div>

      <div class="col-lg-3 col-sm-6 m-b-16">
        <mat-card class="cardWithShadow kpi-card">
          <mat-card-content>
            <div class="kpi-icon bg-light-success">
              <mat-icon class="text-success">event_available</mat-icon>
            </div>
            <div class="kpi-body">
              <div class="kpi-value">{{ kpis?.activeBookings ?? 0 }}</div>
              <div class="kpi-label">Active Bookings</div>
              <div class="kpi-sub text-muted">confirmed, overlapping the period</div>
            </div>
          </mat-card-content>
        </mat-card>
      </div>

      <div class="col-lg-3 col-sm-6 m-b-16">
        <mat-card class="cardWithShadow kpi-card">
          <mat-card-content>
            <div class="kpi-icon bg-light-warning">
              <mat-icon class="text-warning">today</mat-icon>
            </div>
            <div class="kpi-body">
              <div class="kpi-value">{{ kpis?.lessonsToday ?? 0 }}</div>
              <div class="kpi-label">Lessons Today</div>
              <div class="kpi-sub text-muted">today, non-cancelled</div>
            </div>
          </mat-card-content>
        </mat-card>
      </div>

      <div class="col-lg-3 col-sm-6 m-b-16">
        <mat-card class="cardWithShadow kpi-card">
          <mat-card-content>
            <div class="kpi-icon bg-light-accent">
              <mat-icon class="text-accent">inventory_2</mat-icon>
            </div>
            <div class="kpi-body">
              <div class="kpi-value text-accent">
                {{ boxOccupancy?.occupied ?? 0 }}<span class="kpi-value-denom"> / {{ boxOccupancy?.total ?? 0 }}</span>
              </div>
              <div class="kpi-label">Lockers Occupied</div>
              <div class="kpi-sub">
                <span class="text-success">{{ boxOccupancy?.available ?? 0 }} free</span>
                <ng-container *ngIf="(boxOccupancy?.maintenance ?? 0) + (boxOccupancy?.blocked ?? 0) > 0">
                  &nbsp;·&nbsp;<span class="text-error">{{ (boxOccupancy?.maintenance ?? 0) + (boxOccupancy?.blocked ?? 0) }} inactive</span>
                </ng-container>
              </div>
            </div>
          </mat-card-content>
        </mat-card>
      </div>

    </div>

    <!-- ── Courses: Lesson Status + Arrivals + Departures ────────────────────── -->
    <div class="row m-b-16">

      <div class="col-lg-4 m-b-16">
        <mat-card class="cardWithShadow h-100">
          <mat-card-content class="p-24">
            <div class="section-title m-b-20">
              <mat-icon class="text-muted">event_note</mat-icon>
              <span class="f-w-600 f-s-15">Lesson Status</span>
              <span class="section-period text-muted m-l-auto">{{ periodLabel }}</span>
            </div>
            <div class="counter-grid">
              <div class="counter-item">
                <div class="counter-value text-primary">
                  {{ (lessonsToday?.scheduled ?? 0) + (lessonsToday?.inProgress ?? 0) }}
                </div>
                <div class="counter-label">Scheduled</div>
              </div>
              <div class="counter-divider"></div>
              <div class="counter-item">
                <div class="counter-value text-success">{{ lessonsToday?.completed ?? 0 }}</div>
                <div class="counter-label">Completed</div>
              </div>
              <div class="counter-divider"></div>
              <div class="counter-item">
                <div class="counter-value text-error">{{ incidenceToday }}</div>
                <div class="counter-label">Incidence</div>
              </div>
            </div>
          </mat-card-content>
        </mat-card>
      </div>

      <!-- Course Arrivals -->
      <div class="col-lg-4 m-b-16">
        <mat-card class="cardWithShadow h-100">
          <mat-card-content class="p-24">
            <div class="section-title m-b-16">
              <mat-icon class="text-success">flight_land</mat-icon>
              <span class="f-w-600 f-s-15">Arrivals</span>
              <span class="section-badge bg-light-success text-success m-l-auto">
                {{ bookingMovements?.arrivals?.length ?? 0 }}
              </span>
            </div>
            <ng-container *ngIf="bookingMovements?.arrivals?.length; else noCourseArrivals">
              <div class="movements-list">
                <div class="movement-row" *ngFor="let a of bookingMovements.arrivals">
                  <mat-icon class="text-success move-icon">person_add</mat-icon>
                  <div class="movement-info">
                    <span class="movement-name">{{ a.studentName }}</span>
                    <span class="movement-sub text-muted" *ngIf="a.activityName">{{ a.activityName }}</span>
                  </div>
                </div>
              </div>
            </ng-container>
            <ng-template #noCourseArrivals>
              <p class="text-muted f-s-13 m-t-8">No arrivals in period.</p>
            </ng-template>
          </mat-card-content>
        </mat-card>
      </div>

      <!-- Course Departures -->
      <div class="col-lg-4 m-b-16">
        <mat-card class="cardWithShadow h-100">
          <mat-card-content class="p-24">
            <div class="section-title m-b-16">
              <mat-icon class="text-warning">flight_takeoff</mat-icon>
              <span class="f-w-600 f-s-15">Departures</span>
              <span class="section-badge bg-light-warning text-warning m-l-auto">
                {{ bookingMovements?.departures?.length ?? 0 }}
              </span>
            </div>
            <ng-container *ngIf="bookingMovements?.departures?.length; else noCourseDeparts">
              <div class="movements-list">
                <div class="movement-row" *ngFor="let d of bookingMovements.departures">
                  <mat-icon class="text-warning move-icon">person_remove</mat-icon>
                  <div class="movement-info">
                    <span class="movement-name">{{ d.studentName }}</span>
                    <span class="movement-sub text-muted" *ngIf="d.activityName">{{ d.activityName }}</span>
                  </div>
                </div>
              </div>
            </ng-container>
            <ng-template #noCourseDeparts>
              <p class="text-muted f-s-13 m-t-8">No departures in period.</p>
            </ng-template>
          </mat-card-content>
        </mat-card>
      </div>

    </div>

    <!-- ── Courses: Classes ───────────────────────────────────────────────────── -->
    <div class="row m-b-16">
      <div class="col-12">
        <mat-card class="cardWithShadow">
          <mat-card-content class="p-24">
            <div class="section-title m-b-20">
              <mat-icon class="text-muted">school</mat-icon>
              <span class="f-w-600 f-s-15">Classes</span>
              <span class="section-period text-muted m-l-auto">{{ periodLabel }}</span>
            </div>
            <ng-container *ngIf="classesToday.length; else noClasses">
              <div class="classes-table-wrap">
                <table class="classes-table">
                  <thead>
                    <tr>
                      <th>Time</th>
                      <th>Activity</th>
                      <th>Instructor</th>
                      <th class="text-center">Students</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr *ngFor="let c of classesToday">
                      <td class="time-cell">
                        <span class="f-w-600">{{ c.startTime | date:'dd MMM HH:mm' }}</span>
                        <span class="text-muted"> – {{ c.endTime | date:'HH:mm' }}</span>
                      </td>
                      <td>{{ c.activityName || '—' }}</td>
                      <td>{{ c.instructorName || '—' }}</td>
                      <td class="text-center f-w-600">{{ c.studentCount ?? 0 }}</td>
                      <td>
                        <span class="f-w-600" [ngClass]="lessonStatusClass(c.status)">
                          {{ lessonStatusLabel(c.status) }}
                        </span>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </ng-container>
            <ng-template #noClasses>
              <p class="text-muted f-s-13">No classes in selected period.</p>
            </ng-template>
          </mat-card-content>
        </mat-card>
      </div>
    </div>

    <!-- ── Lockers: Check-ins + Check-outs ───────────────────────────────────── -->
    <div class="row m-b-16">

      <div class="col-lg-6 m-b-16">
        <mat-card class="cardWithShadow h-100">
          <mat-card-content class="p-24">
            <div class="section-title m-b-16">
              <mat-icon class="text-success">login</mat-icon>
              <span class="f-w-600 f-s-15">Locker Check-ins</span>
              <span class="section-badge bg-light-success text-success m-l-auto">
                {{ boxMovements?.arrivals?.length ?? 0 }}
              </span>
            </div>
            <ng-container *ngIf="boxMovements?.arrivals?.length; else noCheckins">
              <div class="movements-list">
                <div class="movement-row" *ngFor="let a of boxMovements.arrivals">
                  <span class="box-badge bg-light-success text-success">{{ a.boxLabel }}</span>
                  <span class="movement-name">{{ a.studentName }}</span>
                  <span class="movement-time text-muted">{{ a.arrivedAt | date:'dd MMM HH:mm' }}</span>
                </div>
              </div>
            </ng-container>
            <ng-template #noCheckins>
              <p class="text-muted f-s-13 m-t-8">No locker check-ins in period.</p>
            </ng-template>
          </mat-card-content>
        </mat-card>
      </div>

      <div class="col-lg-6 m-b-16">
        <mat-card class="cardWithShadow h-100">
          <mat-card-content class="p-24">
            <div class="section-title m-b-16">
              <mat-icon class="text-error">logout</mat-icon>
              <span class="f-w-600 f-s-15">Locker Check-outs</span>
              <span class="section-badge bg-light-error text-error m-l-auto">
                {{ boxMovements?.departures?.length ?? 0 }}
              </span>
            </div>
            <ng-container *ngIf="boxMovements?.departures?.length; else noCheckouts">
              <div class="movements-list">
                <div class="movement-row" *ngFor="let d of boxMovements.departures">
                  <span class="box-badge bg-light-error text-error">{{ d.boxLabel }}</span>
                  <span class="movement-name">{{ d.studentName }}</span>
                  <span class="movement-time text-muted">{{ d.freedAt | date:'dd MMM HH:mm' }}</span>
                </div>
              </div>
            </ng-container>
            <ng-template #noCheckouts>
              <p class="text-muted f-s-13 m-t-8">No locker check-outs in period.</p>
            </ng-template>
          </mat-card-content>
        </mat-card>
      </div>

    </div>

    <!-- ── Rentals: Status counters ─────────────────────────────────────────── -->
    <div class="row m-b-16">

      <!-- Rental Activity -->
      <div class="col-lg-4 m-b-16">
        <mat-card class="cardWithShadow h-100">
          <mat-card-content class="p-24">
            <div class="section-title m-b-20">
              <mat-icon class="text-muted">sports_hockey</mat-icon>
              <span class="f-w-600 f-s-15">Rental Activity</span>
            </div>
            <div class="counter-grid">
              <div class="counter-item">
                <div class="counter-value text-primary">{{ rentalKpis?.active ?? 0 }}</div>
                <div class="counter-label">Active</div>
              </div>
              <div class="counter-divider"></div>
              <div class="counter-item">
                <div class="counter-value text-warning">{{ rentalKpis?.reserved ?? 0 }}</div>
                <div class="counter-label">Reserved</div>
              </div>
              <div class="counter-divider"></div>
              <div class="counter-item">
                <div class="counter-value" [class.text-error]="(rentalKpis?.overdue ?? 0) > 0">
                  {{ rentalKpis?.overdue ?? 0 }}
                </div>
                <div class="counter-label">Overdue</div>
              </div>
            </div>
          </mat-card-content>
        </mat-card>
      </div>

      <!-- Products -->
      <div class="col-lg-4 m-b-16" *ngIf="rentalProductStats">
        <mat-card class="cardWithShadow h-100">
          <mat-card-content class="p-24">
            <div class="section-title m-b-20">
              <mat-icon class="text-muted">category</mat-icon>
              <span class="f-w-600 f-s-15">Products</span>
            </div>
            <div class="counter-grid">
              <div class="counter-item">
                <div class="counter-value text-primary">{{ rentalProductStats.total }}</div>
                <div class="counter-label">Total</div>
              </div>
              <div class="counter-divider"></div>
              <div class="counter-item">
                <div class="counter-value text-success">{{ rentalProductStats.active }}</div>
                <div class="counter-label">Active</div>
              </div>
              <div class="counter-divider"></div>
              <div class="counter-item">
                <div class="counter-value">{{ rentalProductStats.hidden }}</div>
                <div class="counter-label">Hidden</div>
              </div>
            </div>
          </mat-card-content>
        </mat-card>
      </div>

      <!-- Equipment -->
      <div class="col-lg-4 m-b-16" *ngIf="equipmentStats">
        <mat-card class="cardWithShadow h-100">
          <mat-card-content class="p-24">
            <div class="section-title m-b-20">
              <mat-icon class="text-muted">inventory_2</mat-icon>
              <span class="f-w-600 f-s-15">Equipment</span>
            </div>
            <div class="counter-grid">
              <div class="counter-item">
                <div class="counter-value text-success">{{ equipmentStats.inService }}</div>
                <div class="counter-label">In Service</div>
              </div>
              <div class="counter-divider"></div>
              <div class="counter-item">
                <div class="counter-value text-warning">{{ equipmentStats.maintenance }}</div>
                <div class="counter-label">Maintenance</div>
              </div>
              <div class="counter-divider"></div>
              <div class="counter-item">
                <div class="counter-value">{{ equipmentStats.retired }}</div>
                <div class="counter-label">Retired</div>
              </div>
            </div>
          </mat-card-content>
        </mat-card>
      </div>

    </div>

    <!-- ── Rentals: Pickups + Returns ─────────────────────────────────────────── -->
    <div class="row m-b-16">

      <div class="col-lg-6 m-b-16">
        <mat-card class="cardWithShadow h-100">
          <mat-card-content class="p-24">
            <div class="section-title m-b-16">
              <mat-icon class="text-primary">output</mat-icon>
              <span class="f-w-600 f-s-15">Rental Pickups</span>
              <span class="section-badge bg-light-primary text-primary m-l-auto">
                {{ rentalMovements?.pickups?.length ?? 0 }}
              </span>
            </div>
            <ng-container *ngIf="rentalMovements?.pickups?.length; else noPickups">
              <div class="movements-list">
                <div class="movement-row" *ngFor="let p of rentalMovements.pickups">
                  <mat-icon class="text-primary move-icon">sports_hockey</mat-icon>
                  <div class="movement-info">
                    <span class="movement-name">{{ p.studentName }}</span>
                    <span class="movement-sub text-muted">{{ p.productName }}</span>
                  </div>
                  <span class="movement-time text-muted">{{ p.startTime | date:'dd MMM HH:mm' }}</span>
                </div>
              </div>
            </ng-container>
            <ng-template #noPickups>
              <p class="text-muted f-s-13 m-t-8">No rental pickups in period.</p>
            </ng-template>
          </mat-card-content>
        </mat-card>
      </div>

      <div class="col-lg-6 m-b-16">
        <mat-card class="cardWithShadow h-100">
          <mat-card-content class="p-24">
            <div class="section-title m-b-16">
              <mat-icon class="text-success">input</mat-icon>
              <span class="f-w-600 f-s-15">Rental Returns</span>
              <span class="section-badge bg-light-success text-success m-l-auto">
                {{ rentalMovements?.returns?.length ?? 0 }}
              </span>
            </div>
            <ng-container *ngIf="rentalMovements?.returns?.length; else noReturns">
              <div class="movements-list">
                <div class="movement-row" *ngFor="let r of rentalMovements.returns">
                  <mat-icon class="text-success move-icon">sports_hockey</mat-icon>
                  <div class="movement-info">
                    <span class="movement-name">{{ r.studentName }}</span>
                    <span class="movement-sub text-muted">{{ r.productName }}</span>
                  </div>
                  <span class="movement-time text-muted">{{ r.returnedAt | date:'dd MMM HH:mm' }}</span>
                </div>
              </div>
            </ng-container>
            <ng-template #noReturns>
              <p class="text-muted f-s-13 m-t-8">No rental returns in period.</p>
            </ng-template>
          </mat-card-content>
        </mat-card>
      </div>

    </div>

    <!-- ── Charts ─────────────────────────────────────────────────────────────── -->
    <div class="row">
      <div class="col-lg-6 m-b-16">
        <app-lessons-stats [data]="lessonsChart"></app-lessons-stats>
      </div>
      <div class="col-lg-6 m-b-16">
        <app-bookings-stats [data]="bookingsChart"></app-bookings-stats>
      </div>
    </div>

  </ng-container>

</div>
